import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BookPlus, Star, X, Loader2, CheckCircle, XCircle, Search, BookOpen } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { usePairwiseRanking } from '@/hooks/usePairwiseRanking';
import { PairwiseSheet } from '@/components/PairwiseSheet';
import { QuickReviewDialog } from '@/components/QuickReviewDialog';
import { MarkAsUnfinishedDialog } from '@/components/MarkAsUnfinishedDialog';
import { SearchReplaceSheet } from '@/components/SearchReplaceSheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface MatchedBook {
  id: string;
  title: string;
  authors: string[];
  cover_url?: string;
  isbn13?: string;
}

interface ExtractedBook {
  extractedTitle: string;
  extractedAuthor: string;
  extractionConfidence: number;
  matchedBook?: MatchedBook;
  matchConfidence?: number;
  isMatched: boolean;
  alreadyInLibrary?: boolean;
  existingShelf?: 'to-read' | 'reading' | 'read' | 'unfinished';
  existingRating?: number;
  userBookId?: string;
}

interface BookshelfScanResultProps {
  books: ExtractedBook[];
  onBooksProcessed?: () => void;
}

interface BookAction {
  id: string;
  status: 'idle' | 'loading' | 'success' | 'skipped';
}

export function BookshelfScanResult({ books, onBooksProcessed }: BookshelfScanResultProps) {
  const { user } = useAuth();
  const [bookActions, setBookActions] = useState<Record<string, BookAction>>({});
  const { pairwiseData, triggerPairwiseComparison, clearPairwiseData } = usePairwiseRanking();
  const [ratingDialogBook, setRatingDialogBook] = useState<{
    title: string;
    author: string;
    matchedId: string;
    extractedTitle: string;
    extractedAuthor: string;
  } | null>(null);
  const [currentRatingBook, setCurrentRatingBook] = useState<{
    id: string;
    book_id: string;
    title: string;
    authors: string[];
    cover_url?: string;
  } | null>(null);
  const [dnfDialogBook, setDnfDialogBook] = useState<ExtractedBook | null>(null);
  const [searchReplaceBook, setSearchReplaceBook] = useState<ExtractedBook | null>(null);
  const [replacedBooks, setReplacedBooks] = useState<Record<string, MatchedBook>>({});
  const [replacementStatuses, setReplacementStatuses] = useState<Record<string, {
    alreadyInLibrary: boolean;
    existingShelf?: 'to-read' | 'reading' | 'read' | 'unfinished';
    existingRating?: number;
    userBookId?: string;
  }>>({});

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 0.8) return 'default';
    if (confidence >= 0.6) return 'secondary';
    return 'outline';
  };

  const handleQuickRate = async (book: ExtractedBook) => {
    if (!user || !book.matchedBook) return;

    let bookDbId = book.matchedBook.id;
    
    // Check if this is a database book (has UUID) vs Open Library book
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(bookDbId);
    
    // If not a UUID, add the book to database first
    if (!isUUID) {
      const { data: existingBook } = await supabase
        .from('books')
        .select('id')
        .eq('open_library_id', book.matchedBook.id)
        .maybeSingle();

      if (existingBook) {
        bookDbId = existingBook.id;
      } else {
        const { data: newBook, error: insertError } = await supabase
          .from('books')
          .insert({
            open_library_id: book.matchedBook.id,
            title: book.matchedBook.title,
            authors: book.matchedBook.authors,
            cover_url: book.matchedBook.cover_url,
            isbn13: book.matchedBook.isbn13,
            source: 'openlibrary'
          })
          .select('id')
          .single();

        if (insertError) {
          console.error('Error adding book to database:', insertError);
          toast.error('Failed to add book to database');
          return;
        }
        bookDbId = newBook.id;
      }
    }

    setRatingDialogBook({
      title: book.matchedBook.title,
      author: book.matchedBook.authors[0] || book.extractedAuthor,
      matchedId: bookDbId,
      extractedTitle: book.extractedTitle,
      extractedAuthor: book.extractedAuthor
    });
  };

  const handleAddToTBR = async (book: ExtractedBook) => {
    if (!user) return;

    const bookId = `${book.extractedTitle}-${book.extractedAuthor}`;
    setBookActions(prev => ({ ...prev, [bookId]: { id: bookId, status: 'loading' } }));

    try {
      if (!book.matchedBook) {
        toast.error(`Could not find "${book.extractedTitle}" in our database`);
        setBookActions(prev => ({ ...prev, [bookId]: { id: bookId, status: 'idle' } }));
        return;
      }

      let bookDbId = book.matchedBook.id;
      
      // Check if this is a database book (has UUID) vs Open Library book
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(bookDbId);
      
      // If not a UUID, add the book to database first
      if (!isUUID) {
        const { data: existingBook } = await supabase
          .from('books')
          .select('id')
          .eq('open_library_id', book.matchedBook.id)
          .maybeSingle();

        if (existingBook) {
          bookDbId = existingBook.id;
        } else {
          const { data: newBook, error: insertError } = await supabase
            .from('books')
            .insert({
              open_library_id: book.matchedBook.id,
              title: book.matchedBook.title,
              authors: book.matchedBook.authors,
              cover_url: book.matchedBook.cover_url,
              isbn13: book.matchedBook.isbn13,
              source: 'openlibrary'
            })
            .select('id')
            .single();

          if (insertError) throw insertError;
          bookDbId = newBook.id;
        }
      }

      const { error } = await supabase.from('user_books').upsert({
        user_id: user.id,
        book_id: bookDbId,
        shelf: 'to-read',
        privacy: 'private',
        is_purchased: true
      }, {
        onConflict: 'user_id,book_id'
      });

      if (error) throw error;

      setBookActions(prev => ({ ...prev, [bookId]: { id: bookId, status: 'success' } }));
      toast.success(`Added "${book.matchedBook.title}" to TBR as owned`);
    } catch (error) {
      console.error('Error adding to TBR:', error);
      toast.error('Failed to add book');
      setBookActions(prev => ({ ...prev, [bookId]: { id: bookId, status: 'idle' } }));
    }
  };

  const handleMarkAsDNF = (book: ExtractedBook) => {
    if (!book.matchedBook) return;
    setDnfDialogBook(book);
  };

  const handleSearchReplace = (book: ExtractedBook) => {
    setSearchReplaceBook(book);
  };

  const handleRate = async (data: { 
    rating: number; 
    reviewText: string; 
    finishedAt?: Date; 
    startedAt?: Date;
    privacy: 'public' | 'private';
    tags?: string[];
  }) => {
    if (!user || !ratingDialogBook) return;

    try {
      const finishedDate = data.finishedAt?.toISOString().split('T')[0];
      const { error: upsertError } = await supabase
        .from('user_books')
        .upsert({
          user_id: user.id,
          book_id: ratingDialogBook.matchedId,
          rating_star: data.rating,
          review_text: data.reviewText || undefined,
          shelf: 'read',
          finished_at: finishedDate,
          started_at: data.startedAt?.toISOString().split('T')[0],
          privacy: data.privacy,
          tags: data.tags || [],
          is_purchased: true
        }, {
          onConflict: 'user_id,book_id'
        });

      if (upsertError) throw upsertError;

      // Mark book as successfully rated immediately using extracted title/author
      const bookKey = `${ratingDialogBook.extractedTitle}-${ratingDialogBook.extractedAuthor}`;
      setBookActions(prev => ({ 
        ...prev, 
        [bookKey]: { id: bookKey, status: 'success' } 
      }));

      const needsComparison = await triggerPairwiseComparison(
        user.id,
        ratingDialogBook.matchedId,
        data.rating
      );

      if (needsComparison) {
        const { data: bookData } = await supabase
          .from('books')
          .select('id, title, authors, cover_url')
          .eq('id', ratingDialogBook.matchedId)
          .single();

        if (bookData) {
          setCurrentRatingBook({
            id: bookData.id,
            book_id: bookData.id,
            title: bookData.title,
            authors: bookData.authors || [],
            cover_url: bookData.cover_url || undefined
          });
        }
      } else {
        toast.success(`Rated "${ratingDialogBook.title}" ${data.rating} stars`);
        setRatingDialogBook(null);
      }
      
      onBooksProcessed?.();
    } catch (error) {
      console.error('Error rating book:', error);
      toast.error('Failed to rate book');
    }
  };

  const handleConfirmDNF = async (data: { currentPage?: number; reason: string }) => {
    if (!user || !dnfDialogBook || !dnfDialogBook.matchedBook) return;

    const bookId = `${dnfDialogBook.extractedTitle}-${dnfDialogBook.extractedAuthor}`;
    setBookActions(prev => ({ ...prev, [bookId]: { id: bookId, status: 'loading' } }));

    try {
      let bookDbId = dnfDialogBook.matchedBook.id;
      
      // Check if this is a database book (has UUID) vs Open Library book
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(bookDbId);
      
      // If not a UUID, we need to add the book to the database first
      if (!isUUID) {
        // Check if book exists by open_library_id
        const { data: existingBook } = await supabase
          .from('books')
          .select('id')
          .eq('open_library_id', dnfDialogBook.matchedBook.id)
          .maybeSingle();

        if (existingBook) {
          bookDbId = existingBook.id;
        } else {
          // Insert the book into the database
          const { data: newBook, error: insertError } = await supabase
            .from('books')
            .insert({
              open_library_id: dnfDialogBook.matchedBook.id,
              title: dnfDialogBook.matchedBook.title,
              authors: dnfDialogBook.matchedBook.authors,
              cover_url: dnfDialogBook.matchedBook.cover_url,
              isbn13: dnfDialogBook.matchedBook.isbn13,
              source: 'openlibrary'
            })
            .select('id')
            .single();

          if (insertError) throw insertError;
          bookDbId = newBook.id;
        }
      }

      // Now mark as DNF
      const { error } = await supabase
        .from('user_books')
        .upsert({
          user_id: user.id,
          book_id: bookDbId,
          shelf: 'unfinished',
          current_page: data.currentPage || undefined,
          review_text: data.reason.trim() || undefined,
          is_purchased: true,
          privacy: 'private'
        }, {
          onConflict: 'user_id,book_id'
        });

      if (error) throw error;

      setBookActions(prev => ({ ...prev, [bookId]: { id: bookId, status: 'success' } }));
      toast.success('Marked as Did Not Finish');
      setDnfDialogBook(null);
    } catch (error) {
      console.error('Error marking as DNF:', error);
      setBookActions(prev => ({ ...prev, [bookId]: { id: bookId, status: 'idle' } }));
      toast.error('Failed to mark as DNF');
    }
  };

  const handleReplaceBook = async (oldBook: ExtractedBook, newBookId: string, newBook: { title: string; authors: string[]; cover_url?: string }) => {
    const bookKey = `${oldBook.extractedTitle}-${oldBook.extractedAuthor}`;
    
    // Check if newBookId is a UUID
    const isUuid = (id: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
    
    // Try to map to local UUID if needed
    let finalId = newBookId;
    if (!isUuid(newBookId)) {
      const { data: mapped } = await supabase
        .from('books')
        .select('id')
        .eq('open_library_id', newBookId)
        .maybeSingle();
      if (mapped?.id) finalId = mapped.id;
    }
    
    // Store the replacement (prefer UUID if found)
    setReplacedBooks(prev => ({
      ...prev,
      [bookKey]: {
        id: finalId,
        title: newBook.title,
        authors: newBook.authors,
        cover_url: newBook.cover_url
      }
    }));
    
    // Check if replaced book exists in user's library
    if (user && isUuid(finalId)) {
      const { data: existing } = await supabase
        .from('user_books')
        .select('id, shelf, rating_star')
        .eq('user_id', user.id)
        .eq('book_id', finalId)
        .maybeSingle();
      
      if (existing) {
        setReplacementStatuses(prev => ({
          ...prev,
          [bookKey]: {
            alreadyInLibrary: true,
            existingShelf: existing.shelf as 'to-read' | 'reading' | 'read' | 'unfinished',
            existingRating: existing.rating_star ?? undefined,
            userBookId: existing.id
          }
        }));
        toast.success(`Already in your library • ${getShelfDisplayName(existing.shelf)}${existing.rating_star ? ` • ★${existing.rating_star}` : ''}`);
      } else {
        setReplacementStatuses(prev => ({
          ...prev,
          [bookKey]: { alreadyInLibrary: false }
        }));
        toast.success('Book replaced successfully');
      }
    } else {
      setReplacementStatuses(prev => ({
        ...prev,
        [bookKey]: { alreadyInLibrary: false }
      }));
      toast.success('Book replaced successfully');
    }
    
    setSearchReplaceBook(null);
    onBooksProcessed?.();
  };

  const handlePairwiseComplete = () => {
    clearPairwiseData();
    setCurrentRatingBook(null);
    if (ratingDialogBook) {
      toast.success(`Ranked "${ratingDialogBook.title}"!`);
    }
    onBooksProcessed?.();
  };

  const handleSkip = (book: ExtractedBook) => {
    const bookId = `${book.extractedTitle}-${book.extractedAuthor}`;
    setBookActions(prev => ({ ...prev, [bookId]: { id: bookId, status: 'skipped' } }));
  };

  const visibleBooks = books.filter(book => {
    const bookId = `${book.extractedTitle}-${book.extractedAuthor}`;
    return bookActions[bookId]?.status !== 'skipped';
  }).map(book => {
    // Apply any replacements and replacement statuses to the book
    const bookKey = `${book.extractedTitle}-${book.extractedAuthor}`;
    const replacement = replacedBooks[bookKey];
    const status = replacementStatuses[bookKey];
    
    let updated = book;
    
    if (replacement) {
      updated = {
        ...updated,
        matchedBook: replacement,
        isMatched: true,
        matchConfidence: 1.0
      };
    }
    
    if (status) {
      updated = {
        ...updated,
        alreadyInLibrary: status.alreadyInLibrary,
        existingShelf: status.existingShelf,
        existingRating: status.existingRating,
        userBookId: status.userBookId
      };
    }
    
    return updated;
  });

  const inLibraryBooks = visibleBooks.filter(b => b.alreadyInLibrary);
  const matchedBooks = visibleBooks.filter(b => b.isMatched && !b.alreadyInLibrary);
  const unmatchedBooks = visibleBooks.filter(b => !b.isMatched && !b.alreadyInLibrary);

  // Helper to get shelf display name
  const getShelfDisplayName = (shelf?: string): string => {
    switch (shelf) {
      case 'to-read': return 'TBR';
      case 'reading': return 'Currently Reading';
      case 'read': return 'Read';
      case 'unfinished': return 'DNF';
      default: return 'Library';
    }
  };

  const handleAddAllToTBR = async () => {
    for (const book of matchedBooks) {
      const bookId = `${book.extractedTitle}-${book.extractedAuthor}`;
      if (!bookActions[bookId] || bookActions[bookId].status === 'idle') {
        await handleAddToTBR(book);
      }
    }
    onBooksProcessed?.();
  };

  if (visibleBooks.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">All books processed!</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle>Detected Books</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {visibleBooks.length} books • {inLibraryBooks.length} already owned • {matchedBooks.length} new matches
            </p>
          </div>
          {matchedBooks.length > 0 && (
            <Button onClick={handleAddAllToTBR} variant="outline" size="sm">
              <BookPlus className="w-4 h-4 mr-2" />
              Add All Matched
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Already in Library Section */}
        {inLibraryBooks.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b">
              <h3 className="text-sm font-semibold text-foreground">Already in Your Library</h3>
              <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-200">
                {inLibraryBooks.length}
              </Badge>
            </div>
            {inLibraryBooks.map((book, index) => {
              const bookKey = `${book.extractedTitle}-${book.extractedAuthor}`;
              const action = bookActions[bookKey] || { id: bookKey, status: 'idle' };
              
              return (
                <div
                  key={index}
                  className="flex gap-4 p-4 rounded-lg border bg-blue-50/50 border-blue-200"
                >
                  <div className="flex-shrink-0 w-16 h-24 bg-muted rounded overflow-hidden">
                    {book.matchedBook?.cover_url ? (
                      <img
                        src={book.matchedBook.cover_url}
                        alt={book.matchedBook.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        <BookPlus className="w-6 h-6" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="mb-2 flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-200">
                        ✓ In Library
                      </Badge>
                      <Badge variant="outline">
                        {getShelfDisplayName(book.existingShelf)}
                      </Badge>
                      {book.existingRating && (
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                          {book.existingRating}
                        </Badge>
                      )}
                    </div>

                    <h4 className="font-semibold text-foreground line-clamp-1">
                      {book.matchedBook?.title || book.extractedTitle}
                    </h4>
                    <p className="text-sm text-muted-foreground line-clamp-1">
                      {book.matchedBook?.authors?.[0] || book.extractedAuthor}
                    </p>

                    <div className="flex gap-2 mt-3">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSearchReplace(book)}
                            >
                              <Search className="w-4 h-4 mr-1" />
                              Search & Replace
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Found the wrong book? Search for the correct one
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Matched books - New Matches */}
        {matchedBooks.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b">
              <h3 className="text-sm font-semibold text-foreground">New Matches</h3>
              <Badge variant="secondary">{matchedBooks.length}</Badge>
            </div>
            {matchedBooks.map((book, index) => {
              const bookId = `${book.extractedTitle}-${book.extractedAuthor}`;
              const action = bookActions[bookId];
              const isProcessed = action?.status === 'success';
              const isLoading = action?.status === 'loading';

              return (
                <div
                  key={`${bookId}-${index}`}
                  className={cn(
                    "flex items-start gap-3 p-3 rounded-lg border transition-colors",
                    isProcessed && "bg-success/5 border-success/20"
                  )}
                >
                  {/* Book cover */}
                  {book.matchedBook?.cover_url && (
                    <div className="w-12 h-16 rounded overflow-hidden border shrink-0 bg-muted">
                      <img
                        src={book.matchedBook.cover_url}
                        alt={book.matchedBook.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm line-clamp-1">
                          {book.matchedBook?.title || book.extractedTitle}
                        </h4>
                        {book.matchedBook && book.matchedBook.title !== book.extractedTitle && (
                          <p className="text-xs text-muted-foreground italic">
                            Detected as: {book.extractedTitle}
                          </p>
                        )}
                      </div>
                      <Badge variant={getConfidenceBadge(book.matchConfidence || 0)} className="text-xs shrink-0">
                        {Math.round((book.matchConfidence || 0) * 100)}%
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      by {book.matchedBook?.authors.join(', ') || book.extractedAuthor}
                    </p>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {isProcessed ? (
                      <div className="flex items-center gap-1.5 text-success px-3 py-1.5 bg-success/10 rounded-md border border-success/20">
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-xs font-medium">Added</span>
                      </div>
                    ) : (
                      <TooltipProvider>
                        <div className="flex gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => handleQuickRate(book)}
                                disabled={isLoading}
                                className="h-8 px-2"
                              >
                                <Star className="w-3 h-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Quick rate</TooltipContent>
                          </Tooltip>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleAddToTBR(book)}
                                disabled={isLoading}
                                className="h-8 px-2"
                              >
                                {isLoading ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <BookPlus className="w-3 h-3" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Add to TBR as owned</TooltipContent>
                          </Tooltip>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleMarkAsDNF(book)}
                                disabled={isLoading}
                                className="h-8 px-2"
                              >
                                <XCircle className="w-3 h-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Mark as DNF</TooltipContent>
                          </Tooltip>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleSearchReplace(book)}
                                disabled={isLoading}
                                className="h-8 px-2"
                              >
                                <Search className="w-3 h-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Search to replace</TooltipContent>
                          </Tooltip>

                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleSkip(book)}
                                disabled={isLoading}
                                className="h-8 px-2"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Skip</TooltipContent>
                          </Tooltip>
                        </div>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Unmatched books */}
        {unmatchedBooks.length > 0 && (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground border-t pt-3">
              <strong>Could not match:</strong>
            </div>
            {unmatchedBooks.map((book, index) => {
              const bookId = `${book.extractedTitle}-${book.extractedAuthor}`;

              return (
                <div
                  key={`unmatched-${bookId}-${index}`}
                  className="flex items-start gap-3 p-3 rounded-lg border border-dashed opacity-60"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <h4 className="font-medium text-sm truncate">{book.extractedTitle}</h4>
                      <Badge variant="outline" className="text-xs shrink-0">
                        No match
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">by {book.extractedAuthor}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleSkip(book)}
                    className="h-8"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <QuickReviewDialog
        isOpen={!!ratingDialogBook}
        onClose={() => setRatingDialogBook(null)}
        book={ratingDialogBook ? {
          id: ratingDialogBook.matchedId,
          title: ratingDialogBook.title,
          author: ratingDialogBook.author
        } : { id: '', title: '', author: '' }}
        onSubmit={handleRate}
      />

      {dnfDialogBook && dnfDialogBook.matchedBook && (
        <MarkAsUnfinishedDialog
          open={!!dnfDialogBook}
          onOpenChange={(open) => !open && setDnfDialogBook(null)}
          onConfirm={handleConfirmDNF}
          bookTitle={dnfDialogBook.matchedBook.title}
          totalPages={0}
          currentPage={0}
          loading={bookActions[`${dnfDialogBook.extractedTitle}-${dnfDialogBook.extractedAuthor}`]?.status === 'loading'}
        />
      )}

      {searchReplaceBook && (
        <SearchReplaceSheet
          isOpen={!!searchReplaceBook}
          onClose={() => setSearchReplaceBook(null)}
          initialQuery={searchReplaceBook.extractedTitle}
          onReplace={(newBookId, newBook) => handleReplaceBook(searchReplaceBook, newBookId, newBook)}
        />
      )}

      {pairwiseData && currentRatingBook && pairwiseData.comparison && (
        <PairwiseSheet
          isOpen={!!pairwiseData}
          onClose={clearPairwiseData}
          currentBook={currentRatingBook}
          tier={pairwiseData.tier}
          candidates={[pairwiseData.comparison.candidateBook]}
          placementState={pairwiseData.placementState}
          comparison={pairwiseData.comparison}
          onComplete={handlePairwiseComplete}
        />
      )}
    </Card>
  );
}
